<template>
  <div class="blog-container">
    <div class="blog-content-wrapper">
      <div v-if="!blogContent" class="blog-form">
        <h2>Create a Blog Post</h2>

        <form @submit.prevent="handleSubmit">
          
          <div class="form-group">
            <label>Blog Title</label>
            <input type="text" v-model="formData.title" required />
          </div>

          
          <div class="form-group">
            <label>Author Name</label>
            <input type="text" v-model="formData.author" required />
          </div>

          
          <div class="form-group">
            <label>Author LinkedIn URL</label>
            <input type="url" v-model="formData.authorLink" required />
          </div>

         
          <div class="form-group">
            <label>Publication Date</label>
            <input type="date" v-model="formData.date" :min="today" :max="today" required />
          </div>

          
          <div class="form-group">
            <label>Tags (comma separated)</label>
            <input type="text" v-model="formData.tags" />
          </div>

          
          <div class="form-group">
            <label>Introduction</label>
            <div class="toolbar">
              <button type="button" @click="toggleBold(editorIntroduction)">Bold</button>
              <button type="button" @click="toggleUnderline(editorIntroduction)">Underline</button>
              <button type="button" @click="toggleBulletList(editorIntroduction)">Bullet List</button>
              <button type="button" @click="toggleOrderedList(editorIntroduction)">Number List</button>
              <button type="button" @click="showEmojiPickerIntro = !showEmojiPickerIntro">😊</button>
            </div>
            <EditorContent v-if="editorIntroduction" :editor="editorIntroduction" class="editor" />
            <vue3-emoji-picker v-if="showEmojiPickerIntro" @emojiClick="emoji => addEmoji(emoji, editorIntroduction, 'Intro')" />
          </div>

          
          <div class="form-group">
            <label>Image/Video 1 URL</label>
            <input type="url" v-model="formData.image1" />
          </div>

          
          <div class="form-group">
            <label>Description</label>
            <div class="toolbar">
              <button type="button" @click="toggleBold(editorDescription)">Bold</button>
              <button type="button" @click="toggleUnderline(editorDescription)">Underline</button>
              <button type="button" @click="toggleBulletList(editorDescription)">Bullet List</button>
              <button type="button" @click="toggleOrderedList(editorDescription)">Number List</button>
              <button type="button" @click="showEmojiPickerDesc = !showEmojiPickerDesc">😊</button>
            </div>
            <EditorContent v-if="editorDescription" :editor="editorDescription" class="editor" />
            <vue3-emoji-picker v-if="showEmojiPickerDesc" @emojiClick="emoji => addEmoji(emoji, editorDescription, 'Desc')" />
          </div>

          
          <div class="form-group">
            <label>Image/Video 2 URL</label>
            <input type="url" v-model="formData.image2" />
          </div>

          
          <div class="form-group">
            <label>Conclusion</label>
            <div class="toolbar">
              <button type="button" @click="toggleBold(editorConclusion)">Bold</button>
              <button type="button" @click="toggleUnderline(editorConclusion)">Underline</button>
              <button type="button" @click="toggleBulletList(editorConclusion)">Bullet List</button>
              <button type="button" @click="toggleOrderedList(editorConclusion)">Number List</button>
              <button type="button" @click="showEmojiPickerCon = !showEmojiPickerCon">😊</button>
            </div>
            <EditorContent v-if="editorConclusion" :editor="editorConclusion" class="editor" />
            <vue3-emoji-picker v-if="showEmojiPickerCon" @emojiClick="emoji => addEmoji(emoji, editorConclusion, 'Con')" />
          </div>

          
          <button type="submit">Create Blog</button>
        </form>
      </div>

      
      <div v-else class="blog-preview">
        <h1>{{ blogContent.title }}</h1>
        <p>
          <strong>By:</strong> <a :href="blogContent.authorLink" target="_blank">{{ blogContent.author }}</a> |
          <strong>Date:</strong> {{ blogContent.date }}
        </p>

        <p><strong>Tags:</strong> {{ blogContent.tags }}</p>

        <div v-html="blogContent.introduction" class="blog-section"></div>

        <div v-if="blogContent.image1" class="blog-image-wrapper">
          <img v-if="isImageUrl(blogContent.image1)" :src="blogContent.image1" alt="Image 1" />
          <video v-else-if="isVideoUrl(blogContent.image1)" controls :src="blogContent.image1"></video>
        </div>

        <div v-html="blogContent.description" class="blog-section"></div>

        <div v-if="blogContent.image2" class="blog-image-wrapper">
          <img v-if="isImageUrl(blogContent.image2)" :src="blogContent.image2" alt="Image 2" />
          <video v-else-if="isVideoUrl(blogContent.image2)" controls :src="blogContent.image2"></video>
        </div>

        <h3>Conclusion</h3>
        <div v-html="blogContent.conclusion" class="blog-section"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import BulletList from '@tiptap/extension-bullet-list'
import OrderedList from '@tiptap/extension-ordered-list'
import ListItem from '@tiptap/extension-list-item'
import EmojiPicker from 'vue3-emoji-picker'
import 'vue3-emoji-picker/css'
import { saveBlogDataToRealtimeDB } from '@/firebase/firebase'

export default {
  name: 'BlogFile',
  components: { EditorContent, Vue3EmojiPicker: EmojiPicker },
  data() {
    return {
      today: new Date().toISOString().split('T')[0],
      formData: {
        title: '',
        author: '',
        authorLink: '',
        date: '',
        tags: '',
        introduction: '',
        image1: '',
        description: '',
        image2: '',
        conclusion: ''
      },
      blogContent: null,
      editorIntroduction: null,
      editorDescription: null,
      editorConclusion: null,
      showEmojiPickerIntro: false,
      showEmojiPickerDesc: false,
      showEmojiPickerCon: false,
    }
  },
  mounted() {
    const extensions = [StarterKit, Underline, BulletList, OrderedList, ListItem]

    this.editorIntroduction = new Editor({
      extensions,
      content: '',
      onUpdate: ({ editor }) => {
        this.formData.introduction = editor.getHTML()
      }
    })
    this.editorDescription = new Editor({
      extensions,
      content: '',
      onUpdate: ({ editor }) => {
        this.formData.description = editor.getHTML()
      }
    })
    this.editorConclusion = new Editor({
      extensions,
      content: '',
      onUpdate: ({ editor }) => {
        this.formData.conclusion = editor.getHTML()
      }
    })
  },
  beforeUnmount() {
    this.editorIntroduction?.destroy()
    this.editorDescription?.destroy()
    this.editorConclusion?.destroy()
  },
  methods: {
    async handleSubmit() {
      try {
        await saveBlogDataToRealtimeDB(this.formData)
        this.blogContent = { ...this.formData }
      } catch (error) {
        console.error('Error saving blog data:', error)
      }
    },
    isImageUrl(url) {
      return /\.(jpeg|jpg|gif|png|webp)$/i.test(url)
    },
    isVideoUrl(url) {
      return /\.(mp4|mov|avi|webm)$/i.test(url)
    },
    toggleBold(editor) {
      editor?.chain().focus().toggleBold().run()
    },
    toggleUnderline(editor) {
      editor?.chain().focus().toggleUnderline().run()
    },
    toggleBulletList(editor) {
      editor?.chain().focus().toggleBulletList().run()
    },
    toggleOrderedList(editor) {
      editor?.chain().focus().toggleOrderedList().run()
    },
    addEmoji(emoji, editor, section) {
      editor.chain().focus().insertContent(emoji.i).run()
      this[`showEmojiPicker${section}`] = false
    }
  }
}
</script>

<style scoped>
.blog-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background-color: #f4f4f4;
  min-height: 100vh;
}
.blog-content-wrapper {
  width: 100%;
  max-width: 800px;
}
.blog-form {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
h2 {
  text-align: center;
  margin-bottom: 20px;
  color: black;
}
.form-group {
  margin-bottom: 20px;
  text-align: left;
}
label {
  font-weight: 600;
  margin-bottom: 8px;
  display: block;
  color: black;
}
input[type="text"],
input[type="url"],
input[type="date"] {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}
button[type="submit"] {
  width: 100%;
  background-color: #2d89ef;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}
button[type="submit"]:hover {
  background-color: #1c66c1;
}
.editor {
  min-height: 150px;
  background: white;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 10px;
  font-size: 14px;
}
.toolbar {
  margin-bottom: 10px;
}
.toolbar button {
  background: #524e4e;
  border: 1px solid #ccc;
  margin-right: 8px;
  padding: 5px 10px;
  border-radius: 6px;
  cursor: pointer;
}
.blog-preview h1 {
  font-size: 32px;
  margin-bottom: 12px;
  color: black;
}
.blog-preview p {
  font-size: 16px;
  margin-bottom: 10px;
  line-height: 1.6;
}
.blog-section {
  margin: 20px 0;
}
.blog-image-wrapper {
  margin: 20px 0;
  text-align: center;
}
.blog-image-wrapper img,
.blog-image-wrapper video {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}
</style>
